/** Binary-mask morphology helpers shared by the selection grow /
 *  shrink path. Masks are stored two ways depending on the call
 *  site:
 *    - `Uint8Array`: 0 = unselected, 1 = selected. Used by the
 *      contour-tracing path (`traceContours` in useFabricCanvas).
 *    - `HTMLCanvasElement`: alpha = 255 where selected, alpha = 0
 *      otherwise. Used for the visual mask the eraser path reads. */

/** Rasterize a polygon's vertices into a Uint8Array binary mask
 *  by filling the path on a transient canvas and reading back the
 *  alpha channel. Self-intersecting polygons resolve via the
 *  even-odd / nonzero fill rule (the `<canvas>` default is
 *  nonzero). */
export function polygonPointsToMask(
  points: { x: number; y: number }[],
  width: number,
  height: number,
): Uint8Array {
  // Negative dims would crash `new Uint8Array(-N)`; clamp to 0 and
  // return a zero-length array (no mask possible at this size).
  // For valid dims with too few points we still return a fully-
  // sized zero mask — callers (grow/shrink) expect the buffer to
  // be `width*height` bytes.
  if (width <= 0 || height <= 0) return new Uint8Array(0);
  const out = new Uint8Array(width * height);
  if (points.length < 3) return out;
  const tmp = document.createElement('canvas');
  tmp.width = width;
  tmp.height = height;
  const ctx = tmp.getContext('2d', { willReadFrequently: true });
  if (!ctx) return out;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 0; i < out.length; i++) {
    out[i] = data[i * 4 + 3] > 127 ? 1 : 0;
  }
  return out;
}

/** Read an alpha-mask canvas back into a `Uint8Array` (1 where
 *  alpha > 127, 0 otherwise). Used to convert the eraser-mask
 *  canvas format into the format `traceContours` expects. */
export function canvasToBinaryMask(canvas: HTMLCanvasElement): {
  mask: Uint8Array;
  width: number;
  height: number;
} {
  // `willReadFrequently` is honored on the FIRST `getContext('2d')`
  // call for a given canvas; the helpers in this file all create
  // their canvases with that hint, so this re-fetch is a no-op for
  // them. For external callers (e.g. wand creation in
  // useFabricCanvas) the canvas is created with the hint at its
  // origin, so the read here is fast either way.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { mask: new Uint8Array(0), width: 0, height: 0 };
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const mask = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = data[i * 4 + 3] > 127 ? 1 : 0;
  }
  return { mask, width: canvas.width, height: canvas.height };
}

/** Convert a `Uint8Array` mask back to an alpha-mask canvas (white
 *  pixels where selected, transparent otherwise). Used to refresh
 *  the cached wandMaskCanvas after a morphology op. The canvas is
 *  created with `willReadFrequently` because downstream code
 *  reads it back via `canvasToBinaryMask`. */
export function binaryMaskToCanvas(
  mask: Uint8Array,
  width: number,
  height: number,
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return out;
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      const di = i * 4;
      data[di] = 255;
      data[di + 1] = 255;
      data[di + 2] = 255;
      data[di + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return out;
}

/** Threshold for the canvas-blur dilation/erosion technique. After
 *  blurring a binary mask, alpha values fall on a Gaussian
 *  curve from 255 (interior) to 0 (far outside). A threshold of
 *  ~12.5% (32/255) lands the dilated boundary at approximately
 *  the requested radius. Tuned empirically against the brute-
 *  force kernel result on typical lasso shapes — within ±1px. */
const BLUR_THRESHOLD = 32;

/** Internal: blur a binary-mask canvas via the GPU-accelerated CSS
 *  filter, then threshold back to binary. Used by both dilate and
 *  erode (erosion = invert + dilate + invert). */
function blurAndThreshold(source: HTMLCanvasElement, blurRadius: number): HTMLCanvasElement | null {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.filter = `blur(${blurRadius}px)`;
  ctx.drawImage(source, 0, 0);
  ctx.filter = 'none';
  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] > BLUR_THRESHOLD) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    } else {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return out;
}

/** Invert a binary-mask canvas (white where transparent, transparent
 *  where opaque). Used by `erodeMask` via the invert + dilate +
 *  invert identity. */
function invertMaskCanvas(source: HTMLCanvasElement): HTMLCanvasElement | null {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  // Fill solid white, then knock out the source via destination-out
  // so the transparent regions of `source` become opaque white in
  // `out` and vice versa.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.drawImage(source, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

/** Dilate a binary mask by approximately `radius` pixels.
 *
 *  Implementation: the source mask is blurred with a CSS Gaussian
 *  filter — GPU-accelerated, O(W·H) regardless of radius — then
 *  thresholded back to binary. The threshold is tuned so the
 *  dilated boundary lands ~1px of the brute-force Euclidean disk
 *  result. ~50–100× faster than the brute-force kernel for typical
 *  selections; matters most at radius ≥ 8 on multi-megapixel
 *  selections where the brute force tips into seconds.
 *
 *  The Gaussian falloff means the dilation is rotationally smooth
 *  but not a perfect disk — fine for "grow selection by N" UX
 *  (where the user judges by eye, not pixel-exact), and within ±1px
 *  of the disk result on typical lasso shapes. */
export function dilateMask(
  mask: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) return new Uint8Array(mask);
  const sourceCanvas = binaryMaskToCanvas(mask, width, height);
  const dilated = blurAndThreshold(sourceCanvas, r);
  if (!dilated) return new Uint8Array(mask);
  return canvasToBinaryMask(dilated).mask;
}

/** Erode a binary mask by approximately `radius` pixels via the
 *  invert + dilate + invert identity. Same speed profile as
 *  `dilateMask`. Out-of-bounds pixels count as "out" — so erosion
 *  conservatively pulls back from the canvas edge, matching what
 *  users expect ("don't shrink against an unknown beyond"). The
 *  identity holds because: shrinking the mask by R pixels =
 *  growing the COMPLEMENT by R pixels, then complementing again. */
export function erodeMask(
  mask: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) return new Uint8Array(mask);
  const sourceCanvas = binaryMaskToCanvas(mask, width, height);
  const inverted = invertMaskCanvas(sourceCanvas);
  if (!inverted) return new Uint8Array(mask);
  const dilatedInverse = blurAndThreshold(inverted, r);
  if (!dilatedInverse) return new Uint8Array(mask);
  const final = invertMaskCanvas(dilatedInverse);
  if (!final) return new Uint8Array(mask);
  return canvasToBinaryMask(final).mask;
}
